/* eslint-disable react/jsx-handler-names */
"use client";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ImageTile from "../../../atoms/imageTile";
import {
  CALENDAR,
  COUNTRIES,
  CURRENCY_ID,
  DELIVERY_METHOD,
  DELIVERY_METHOD_WITH_CALENDAR,
  DELIVERY_METHOD_WITH_TIMESLOTS,
  SELECT_DELIVERY_DATE,
  SELECT_SHIPPING_METHOD,
  SELECT_SHIPPING_METHOD_AND_DATE,
  SELECT_SHIPPING_METHOD_AND_TIME_SLOT,
} from "../../../../constants/common/common";
import Calendar from "../../../molecules/calendar/calendar";
import ShippingMethods from "../../../molecules/shippingMethods/shippingMethods";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  requestToFetchGetDeliveryDateAndGeoPriceAction,
  resetGetDeliveryDateAndGeoPriceAction,
  setDeliveryDetailsAction,
} from "../../../../store/slices/common/shippingDateAndSlotSelection";
import ButtonComponent from "../../../atoms/button";
import useAnalytics from "../../../../analytics/useAnalytics";
import { setGaDateSelection } from "../../../../analytics/gaEvents";
import { cleverTapCommonEvents } from "../../../../analytics/clevertapConstants";
import { GA4_EVENTS } from "../../../../analytics/gaConstant";
import useCleverTapCartData from "../../../../customHooks/clevertap/cart";
import { isPersonalizedProduct } from "../../../../utils/pdp/common";
import { productTypes } from "../../../../constants/pageConstants/pdpConstants";
import Typography from "../../../atoms/typography";
import CustomImage from "../../../atoms/customImage";
import SurgePriceDrawer from "./surgePriceDrawer";
import { dateToDMY, isDateTodayOrTomorrow } from "../../../../utils/common/deliveryDate";
import { formatClassNames } from "../../../../utils/common";

/**
 * Represents InternationalShippingDateAndSlotSelection component for shipping method,selecting date and time slot.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.onHandleDeliveryDetailsClick handles the delivery details section click to edit the details and also going back to cart from Calendar.
 * @returns {React.ReactElement} A React element displaying the Calendar component.
 */
const InternationalShippingDateAndSlotSelection = ({
  onToggleDeliveryDetailsPopUp,
  currentItem,
  classes,
  isCart = false,
  isMobile,
  selectedCurrency,
  popupCleverTapData,
  cartCleverTapData,
  isRakhiDelivery,
}) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const isInternational = currentItem?.catalogId !== COUNTRIES.INDIA;

  const [surgeDates, setSurgeDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [surgePriceDrawer, setSurgePriceDrawer] = useState(false);
  const [isSameDateSelected, setIsSameDateSelected] = useState(false);
  const [currentShippingMethod, setCurrentShippingMethod] = useState({});
  const [currentComponent, setCurrentComponent] = useState(
    isRakhiDelivery ? DELIVERY_METHOD_WITH_CALENDAR : DELIVERY_METHOD,
  );
  const [isSameShippingSelected, setIsSameShippingSelected] = useState(false);
  const [userSelectedTimeSlotId, setUserSelectedTimeSlotId] = useState(null);

  const {
    deliveryType,
    surgePriceDetails,
    clientVariantType,
    selectedIntLocation,
    nextAvailableDifference,
    updatedDeliveryDatesAndGeoPrice,
  } = useSelector(
    (state) => ({
      deliveryType: state?.pdp?.deliveryType,
      surgePriceDetails: state.pdp.surgePriceDetails,
      clientVariantType: state?.pdp?.clientVariantType,
      selectedIntLocation: state?.locationLock?.selectedIntLocation,
      nextAvailableDifference: state?.shippingDateAndSlotSelection?.nextAvailableDifference,
      updatedDeliveryDatesAndGeoPrice: state?.shippingDateAndSlotSelection?.updatedDeliveryDatesAndGeoPrice,
    }),
    shallowEqual,
  );

  const courierType = deliveryType === productTypes.COURIER;
  const { isDateToday, isDateTomorrow } = isDateTodayOrTomorrow(new Date(currentItem?.deliveryDate));
  const classForDesktop = isRakhiDelivery && !isMobile && !isCart;
  const classForMobile = isRakhiDelivery && (isMobile || isCart);
  const showTimeSlots = currentComponent === DELIVERY_METHOD_WITH_TIMESLOTS;

  //surge price implementation
  useEffect(() => {
    if (surgePriceDetails && surgePriceDetails?.eventDates) {
      const formattedDates = surgePriceDetails?.eventDates?.map((date) => dateToDMY(new Date(date)));
      setSurgeDates(formattedDates);
    }
  }, [surgePriceDetails]);

  const cleverTapHooksData = useCleverTapCartData({
    currentItem,
    clientVariantType,
    nextAvailableDifference,
    isSameDateSelected,
    isSameShippingSelected,
    userSelectedTimeSlotId,
    isDateToday,
    isDateTomorrow,
  });

  const clevertapDeliveryData = {
    ...cleverTapHooksData,
    ...cartCleverTapData,
  };

  /**
   * This function for handling click on the drawer back button.
   *
   * @function
   * @returns {void}
   */
  const onHandleBackButtonClick = () => {
    switch (currentComponent) {
      case DELIVERY_METHOD:
      case DELIVERY_METHOD_WITH_CALENDAR:
        onToggleDeliveryDetailsPopUp();
        break;
      case CALENDAR:
        setSelectedDate("");
        setCurrentComponent(DELIVERY_METHOD);
        break;
      case DELIVERY_METHOD_WITH_TIMESLOTS:
        setCurrentComponent(isRakhiDelivery ? DELIVERY_METHOD_WITH_CALENDAR : CALENDAR);
        break;
    }
  };

  /**
   * Function to get the header title for the delivery details process.
   *
   * @function
   * @returns {string} The header title for the delivery details process.
   */
  const headerTitleForDeliveryDetails = () => {
    switch (currentComponent) {
      case CALENDAR:
        return SELECT_DELIVERY_DATE;
      case DELIVERY_METHOD:
        return SELECT_SHIPPING_METHOD;
      case DELIVERY_METHOD_WITH_TIMESLOTS:
        return SELECT_SHIPPING_METHOD_AND_TIME_SLOT;
      case DELIVERY_METHOD_WITH_CALENDAR:
        return SELECT_SHIPPING_METHOD_AND_DATE;
    }
  };

  /**
   * Function to toggle the delivery details pop-up and track the event when it is closed.
   * It calls the `onToggleDeliveryDetailsPopUp` function to change the state of the pop-up
   * and then triggers a CleverTap event to record the closure of the pop-up.
   *
   * @function
   * @returns {void} This function does not return any value.
   */
  const onHandleDeliveryDetailsPopupToggle = () => {
    onToggleDeliveryDetailsPopUp();
    trackEvent(cleverTapCommonEvents.deliveryPopupClosed, popupCleverTapData, ["clevertap"]);
  };

  /**
   * Handles the selection of a date in the calendar.
   * After selecting a date, it sets the selected date in state and moves the current component to the delivery method.
   *
   * @function
   * @param {string} e - The selected date from the calendar.
   * @returns {void}
   */
  const onDateSelection = (date) => {
    const isSurge = surgeDates?.includes(date);

    if (isSurge) {
      const queryParams = {
        productId: currentItem?.productId,
        pinCode: isCart ? currentItem?.pincode : selectedIntLocation?.pincode,
        currencyUomId: CURRENCY_ID,
        deliveryDate: date,
        catalogId: isCart ? currentItem?.productCatalogId : currentItem?.catalogId,
      };

      dispatch(requestToFetchGetDeliveryDateAndGeoPriceAction(queryParams));
      isMobile ? setSurgePriceDrawer(true) : setCurrentComponent(DELIVERY_METHOD_WITH_TIMESLOTS);
    } else {
      dispatch(resetGetDeliveryDateAndGeoPriceAction());
      setCurrentComponent(DELIVERY_METHOD_WITH_TIMESLOTS);
    }

    setSelectedDate(date);
    trackEvent(
      GA4_EVENTS?.DELIVERY_DATE_SELECTION,
      {
        ...setGaDateSelection(date, "pdp_interaction", GA4_EVENTS?.DELIVERY_DATE_SELECTION),
      },
      ["ga4"],
    );
    if (isCart) {
      setIsSameDateSelected(date === currentItem?.deliveryDate);
      trackEvent(cleverTapCommonEvents.deliveryDateSelected, { ...clevertapDeliveryData, date_of_delivery: date }, [
        "clevertap",
      ]);
    } else {
      trackEvent(cleverTapCommonEvents.deliveryDateSelected, { ...popupCleverTapData, date_of_delivery: date }, [
        "clevertap",
      ]);
    }
  };

  /**
   * Handles the selection of time and shipping method.
   * Combines the selected time and shipping method with the selected date, and calls the onHandleDeliveryDetailsClick callback.
   *
   * @function
   * @param {Object} timeNShippingMethod - An object containing the selected time slot and shipping method.
   * @returns {void}
   */
  const onTimeAndShippingMethodSelection = (timeNShippingMethod) => {
    dispatch(setDeliveryDetailsAction({ ...timeNShippingMethod, selectedDate }));
    onToggleDeliveryDetailsPopUp(timeNShippingMethod, selectedDate);
  };

  return (
    <div
      className={`flex w-[100%] flex-col transition-all ${isRakhiDelivery ? "gap-0" : "gap-10"} ${showTimeSlots && classForDesktop ? "md:h-[475px] md:w-500" : classes}`}
    >
      <div className="flex items-center justify-start gap-16 border-b-1 border-[#F5F5F5] px-5 py-16 pb-16">
        <div className={`flex w-full ${isMobile ? "gap-12" : isCart ? "gap-16" : "justify-between"} items-center `}>
          <div
            className={`cursor-pointer ${classForDesktop && !showTimeSlots ? "invisible" : ""}`}
            onClick={classForDesktop && !showTimeSlots ? null : onHandleBackButtonClick}
          >
            <ImageTile imgSrc={"/icons/left-arrow.svg"} imgHeight={20} imgWidth={20} imgAlt="Left Arrow" />
          </div>
          <div className="top-2 text-16 font-600 text-black-100" data-testid="cart">
            {headerTitleForDeliveryDetails()}
          </div>
          {!isMobile && !isCart && (
            <div className="cursor-pointer">
              <ButtonComponent
                buttonVariant="icon"
                icon="/icons/close-circle-black.svg"
                onClick={onHandleDeliveryDetailsPopupToggle}
                classes="border-0"
                size="large"
              />
            </div>
          )}
        </div>
      </div>
      <div
        className={formatClassNames(
          "custom-scrollbar container mx-auto overflow-y-auto",
          classForDesktop && "flex h-full p-0",
          classForMobile ? "px-0 pb-16 pt-0" : !classForDesktop ? "px-20 pb-20 pt-16" : "",
        )}
        id="shipping-method-root"
      >
        {(currentComponent === DELIVERY_METHOD ||
          currentComponent === DELIVERY_METHOD_WITH_TIMESLOTS ||
          currentComponent === DELIVERY_METHOD_WITH_CALENDAR) && (
          <ShippingMethods
            currentItem={currentItem}
            onTimeAndShippingMethodSelection={onTimeAndShippingMethodSelection}
            setCurrentComponent={setCurrentComponent}
            currentComponent={currentComponent}
            showTimeSlots={currentComponent === DELIVERY_METHOD_WITH_TIMESLOTS}
            setCurrentShippingMethod={setCurrentShippingMethod}
            currentShippingMethod={currentShippingMethod}
            isInternational={isInternational}
            selectedCurrency={selectedCurrency}
            popupCleverTapData={popupCleverTapData}
            cartCleverTapData={clevertapDeliveryData}
            isCart={isCart}
            setIsSameShippingSelected={setIsSameShippingSelected}
            setUserSelectedTimeSlotId={setUserSelectedTimeSlotId}
            selectedDate={selectedDate}
            onDateSelection={onDateSelection}
            surgeDates={surgeDates}
            isMobile={isMobile}
            isRakhiDelivery={isRakhiDelivery}
          />
        )}
        {currentComponent === CALENDAR && !isRakhiDelivery && (
          <>
            <Calendar
              isInternational={isInternational}
              onDateSelection={onDateSelection}
              selectedDate={selectedDate}
              isPersonalizedProduct={isPersonalizedProduct(currentItem)}
              isCourierProduct={courierType}
              surgeDates={surgeDates}
            />

            {surgeDates.length > 0 && !courierType && !isPersonalizedProduct(currentItem) && (
              <div className="mt-[16px] flex gap-4 p-4">
                <CustomImage width={16} height={16} src="/icons/price-surge-icon.svg" alt="price_surge_icon" />
                <Typography Tag="p" classes="text-[12px] text-[#444] font-inter font-medium leading-[16px] normal-case">
                  {surgePriceDetails?.legendMessage || ""}
                </Typography>
              </div>
            )}
          </>
        )}
        <SurgePriceDrawer
          isOpen={surgePriceDrawer}
          onClose={() => setSurgePriceDrawer(false)}
          surgePriceDetails={surgePriceDetails}
          selectedCurrency={selectedCurrency}
          updatedDeliveryDatesAndGeoPrice={updatedDeliveryDatesAndGeoPrice}
          onContinue={() => {
            setSurgePriceDrawer(false);
            setCurrentComponent(DELIVERY_METHOD_WITH_TIMESLOTS);
          }}
        />
      </div>
    </div>
  );
};
InternationalShippingDateAndSlotSelection.propTypes = {
  onToggleDeliveryDetailsPopUp: PropTypes.func.isRequired,
  currentItem: PropTypes.object.isRequired,
  classes: PropTypes.string,
  isCart: PropTypes.bool,
  isMobile: PropTypes.bool,
  selectedCurrency: PropTypes.object,
  popupCleverTapData: PropTypes.object,
  cartCleverTapData: PropTypes.object,
  isRakhiDelivery: PropTypes.bool,
};
export default InternationalShippingDateAndSlotSelection;
